'use client';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useHeader } from '@/components/complex/header';
import { useBottomNavigation } from '@/components/complex/bottom-navigation';
import { useRouter } from 'next/navigation';
import api from '@/libs/api';
import { AutocompleteResponse, RecentSearchDto } from '@/api/search';
import SearchMainView from '@/screen/feed/view/search/main';
import SearchResultView from '@/screen/feed/view/search/result';
import { PC_MEDIA_QUERY } from '@/libs/config/responsive';

interface SearchScreenProps {
  keyword?: string;
}

const SearchScreen = ({ keyword: initialKeyword }: SearchScreenProps) => {
  const router = useRouter();
  const autoFocus = !initialKeyword;
  const [focus, setFocus] = useState(false);
  const [keyword, setKeyword] = useState(initialKeyword || '');
  const [autocomplete, setAutocomplete] = useState<AutocompleteResponse | null>(null);
  const [recentSearches, setRecentSearches] = useState<RecentSearchDto[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const autocompleteRef = useRef<AutocompleteResponse | null>(null);
  const recentSearchesRef = useRef<RecentSearchDto[]>([]);
  const selectedIndexRef = useRef(-1);

  useBottomNavigation(true);

  const resetSelectedIndex = useCallback(() => {
    selectedIndexRef.current = -1;
    setSelectedIndex(-1);
  }, []);

  const handleSearch = useCallback((searchValue: string) => {
    if (searchValue.trim()) {
      router.replace(`/search?keyword=${encodeURIComponent(searchValue)}`);
      setKeyword(searchValue);
      setFocus(false);
      resetSelectedIndex();
    }
  }, [resetSelectedIndex, router]);

  const handleFocus = useCallback(() => {
    resetSelectedIndex();
    setFocus(true);
  }, [resetSelectedIndex]);

  const handleBlur = useCallback(() => {
    setFocus(false);
  }, []);

  const handleSearchKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>, value: string) => {
    if (!window.matchMedia(PC_MEDIA_QUERY).matches) return;

    const items = value ? (autocompleteRef.current?.items || []) : recentSearchesRef.current;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((current) => {
        const next = Math.min(current + 1, items.length - 1);
        selectedIndexRef.current = next;
        return next;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((current) => {
        const next = Math.max(current - 1, -1);
        selectedIndexRef.current = next;
        return next;
      });
      return;
    }

    const selectedItem = items[selectedIndexRef.current];
    if (event.key === 'Enter' && selectedItem) {
      event.preventDefault();
      handleSearch(selectedItem.keyword);
      event.currentTarget.blur();
    }
  }, [handleSearch]);

  const { searchKeyword, setHeader } = useHeader({
    useBack: true,
    desktopUseBack: false,
    desktopTitle: '검색',
    searchMode: true,
    searchValue: keyword,
    autoFocus,
    onSearch: handleSearch,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleSearchKeyDown,
  });

  const onSelectKeyword = useCallback((selected: string) => {
    setKeyword(selected);
    setHeader({
      useBack: true,
      desktopUseBack: false,
      desktopTitle: '검색',
      searchMode: true,
      searchValue: selected,
      onSearch: handleSearch,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleSearchKeyDown,
    });
    handleSearch(selected);
  }, [handleSearch, setHeader, handleFocus, handleBlur, handleSearchKeyDown]);

  const onDeleteRecentSearch = useCallback(async (keyword: string) => {
    try {
      await api.search.deleteRecentSearch(keyword);
      setRecentSearches(prev => prev.filter(item => item.keyword !== keyword));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    setKeyword(initialKeyword || '');
  }, [initialKeyword]);

  useEffect(() => {
    autocompleteRef.current = autocomplete;
    recentSearchesRef.current = recentSearches;
  }, [autocomplete, recentSearches]);

  useEffect(() => {
    resetSelectedIndex();
  }, [resetSelectedIndex, searchKeyword]);

  useEffect(() => {
    const fetchAutocomplete = async () => {
      try {
        if (searchKeyword) {
          const res = await api.search.autocomplete(searchKeyword);
          setAutocomplete(res);
        } else {
          setAutocomplete(null);
          const res = await api.search.getRecentSearches();
          setRecentSearches(res);
        }
      } catch (e) {
        console.error(e);
      }
    };

    const timer = setTimeout(async () => {
      await fetchAutocomplete();
    }, 200);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  return (
    <>
      {focus ? <SearchMainView
        searchKeyword={searchKeyword || ''}
        autocomplete={autocomplete}
        recentSearches={recentSearches}
        selectedIndex={selectedIndex}
        onSearch={onSelectKeyword}
        onDeleteRecentSearch={onDeleteRecentSearch}
      /> : <></>}
      <SearchResultView keyword={keyword} />
    </>
  );
};

export default SearchScreen;
